/**
 * `useRealtime` — hook React de consumo del Realtime Hub para la Capa
 * Operacional AI-First.
 *
 * Refleja los Eventos_Operacionales en las vistas por **APPEND
 * INCREMENTAL** en orden temporal no decreciente (R7.4): cada evento
 * entregado por el Hub se anexa al final del flujo sin recomputar el
 * estado completo, y se materializa el **último estado conocido** de
 * cada entidad suscrita.
 *
 * Degradación de la Suscripcion_Realtime (R1.5, R1.9): ante una
 * interrupción del canal (el navegador pasa a `offline`, o el consumidor
 * la fuerza), el hook marca los estados como **posiblemente obsoletos**
 * (`obsoleto = true`) y **preserva los últimos estados conocidos** en
 * lugar de descartarlos; al restablecerse la conexión reanuda la entrega
 * vía `resume` (que internamente decide resume ≤60s vs re-sync >60s,
 * R7.7/R7.8) y limpia la marca de obsolescencia.
 *
 * Consume el Hub vía {@link getRealtimeHub} (que se auto-registra en el
 * runtime selector, R17.1/R17.5). Toda la política de filtro, orden,
 * RBAC y reanudación vive en `realtimeHub.ts` (tarea 5.1); este hook solo
 * **cablea** ese stream al ciclo de vida de React.
 *
 * Nota de alcance: este hook y el componente `Ticker` son reutilizables y
 * quedan listos; su integración en las vistas (LiveOperations,
 * OperationalMap, AlertCenter) corresponde a la tarea 5.5.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
  CanalRealtime,
  EntidadRef,
  EventoOperacional,
  FiltroRealtime,
} from "../api/contracts";
import { getRealtimeHub, type EventoConSeq } from "./realtimeHub";

/** Número máximo de eventos retenidos en el flujo del Ticker por defecto. */
export const MAX_EVENTOS_TICKER = 200;

/** Clave estable de una entidad (`tipo:id`) para indexar el último estado. */
function entityKey(ref: EntidadRef): string {
  return `${ref.tipo}:${ref.id}`;
}

/**
 * Último estado conocido de una entidad suscrita, derivado del último
 * Evento_Operacional que la afectó. Se **preserva** durante una
 * interrupción de la suscripción (R1.9).
 */
export interface EstadoConocido {
  entidad_ref: EntidadRef;
  /** Tipo del último Evento_Operacional aplicado a la entidad. */
  tipo_evento: string;
  /** Payload del último evento (estado vigente conocido). */
  estado: Record<string, unknown>;
  /** Instante (ISO-8601 UTC) del último evento conocido. */
  ts: string;
  /** Secuencia global del último evento conocido. */
  seq_global: number;
}

/** Opciones del hook {@link useRealtime}. */
export interface UseRealtimeOptions {
  /**
   * Máximo de eventos retenidos en el flujo (append incremental). Los más
   * antiguos se descartan al superar el límite. Default {@link MAX_EVENTOS_TICKER}.
   */
  maxEventos?: number;
  /**
   * Si es `false`, no se suscribe (útil para diferir hasta que el filtro
   * esté listo). Default `true`.
   */
  enabled?: boolean;
  /**
   * Vincula la marca de obsolescencia a los eventos `online`/`offline`
   * del navegador (R1.9). Default `true`.
   */
  reaccionarRedNavegador?: boolean;
}

/** Estado expuesto por {@link useRealtime}. */
export interface UseRealtimeState {
  /** Flujo de eventos en orden de llegada (append incremental, R7.4). */
  eventos: EventoConSeq[];
  /** Último estado conocido por entidad suscrita (preservado, R1.9). */
  estados: Map<string, EstadoConocido>;
  /** True si la suscripción está interrumpida → estados posiblemente obsoletos (R1.9). */
  obsoleto: boolean;
  /** True mientras el canal está abierto y entregando. */
  conectado: boolean;
  /** Error de suscripción (p. ej. filtro inválido, R7.3). */
  error: string | null;
  /** Fuerza una interrupción (marca obsoleto y preserva el último estado). */
  marcarObsoleto: () => void;
  /** Reanuda la entrega desde el último evento conocido (R7.7/R7.8). */
  reconectar: () => void;
  /** Vacía el flujo de eventos sin perder los últimos estados conocidos. */
  limpiar: () => void;
}

/**
 * Suscribe la vista al Realtime Hub con `filtro` y refleja los eventos
 * por append incremental, manejando la degradación de la suscripción.
 */
export function useRealtime(
  filtro: FiltroRealtime,
  options: UseRealtimeOptions = {},
): UseRealtimeState {
  const {
    maxEventos = MAX_EVENTOS_TICKER,
    enabled = true,
    reaccionarRedNavegador = true,
  } = options;

  const [eventos, setEventos] = useState<EventoConSeq[]>([]);
  const [estados, setEstados] = useState<Map<string, EstadoConocido>>(new Map());
  const [obsoleto, setObsoleto] = useState(false);
  const [conectado, setConectado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cursor del último evento entregado, para reanudar sin pérdida/duplicado (R7.7).
  const lastSeqRef = useRef(0);
  const canalRef = useRef<CanalRealtime | null>(null);

  // Clave estable del filtro para no re-suscribir en cada render.
  const filtroKey = useMemo(() => JSON.stringify(filtro ?? null), [filtro]);

  /** Anexa un evento entregado, en orden de llegada, y actualiza el último estado. */
  const onEvento = useCallback(
    (evento: EventoOperacional, seq_global: number) => {
      lastSeqRef.current = Math.max(lastSeqRef.current, seq_global);

      // Append incremental del flujo, acotado por maxEventos (R7.4).
      setEventos((prev) => {
        const next = [...prev, { evento, seq_global }];
        return next.length > maxEventos ? next.slice(next.length - maxEventos) : next;
      });

      // Materializa el último estado conocido de la entidad afectada (R1.5).
      setEstados((prev) => {
        const next = new Map(prev);
        next.set(entityKey(evento.entidad_ref), {
          entidad_ref: evento.entidad_ref,
          tipo_evento: evento.tipo,
          estado: evento.payload,
          ts: evento.ts,
          seq_global,
        });
        return next;
      });

      // La llegada de un evento confirma que el canal está vivo (R1.9).
      setObsoleto(false);
    },
    [maxEventos],
  );

  useEffect(() => {
    if (!enabled) {
      setConectado(false);
      return undefined;
    }

    const hub = getRealtimeHub();
    let cancelado = false;
    let desuscribir: () => void = () => {};

    setError(null);

    void hub
      .subscribe(filtro)
      .then((res) => {
        if (cancelado) return;
        if (!res.ok) {
          // R7.3: filtro inválido → no se abre canal, se expone el error.
          setError(res.error);
          setConectado(false);
          return;
        }
        canalRef.current = res.value;
        desuscribir = hub.listen(
          res.value.canal_id,
          onEvento,
          // R7.8: re-sync — reconstruye el último estado conocido por replay.
          (reconstruidos) => {
            setEstados((prev) => {
              const next = new Map(prev);
              for (const r of reconstruidos) {
                const clave = entityKey(r.entidad_ref);
                const previo = next.get(clave);
                next.set(clave, {
                  entidad_ref: r.entidad_ref,
                  tipo_evento: previo?.tipo_evento ?? "resync",
                  estado: r.estado,
                  ts: previo?.ts ?? new Date().toISOString(),
                  seq_global: previo?.seq_global ?? 0,
                });
              }
              return next;
            });
          },
        );
        setConectado(true);
        setObsoleto(false);
      })
      .catch((e: unknown) => {
        if (cancelado) return;
        setError(e instanceof Error ? e.message : String(e));
        setConectado(false);
      });

    return () => {
      cancelado = true;
      desuscribir();
      setConectado(false);
    };
    // onEvento es estable salvo cambio de maxEventos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroKey, enabled, onEvento]);

  /** Reanuda la entrega desde el último evento conocido (R7.7/R7.8). */
  const reconectar = useCallback(() => {
    const canal = canalRef.current;
    if (!canal) return;
    const hub = getRealtimeHub();
    void hub
      .resume(canal.canal_id, lastSeqRef.current)
      .then((res) => {
        if (res.ok) {
          setObsoleto(false);
          setConectado(true);
          setError(null);
        } else {
          setError(res.error);
        }
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      });
  }, []);

  /** Marca la suscripción como interrumpida; preserva los últimos estados (R1.9). */
  const marcarObsoleto = useCallback(() => {
    setObsoleto(true);
  }, []);

  const limpiar = useCallback(() => {
    setEventos([]);
  }, []);

  // Vincula la obsolescencia a la conectividad del navegador (R1.9, R7.7/R7.8).
  useEffect(() => {
    if (!enabled || !reaccionarRedNavegador || typeof window === "undefined") {
      return undefined;
    }
    const onOffline = () => setObsoleto(true);
    const onOnline = () => reconectar();
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, [enabled, reaccionarRedNavegador, reconectar]);

  return {
    eventos,
    estados,
    obsoleto,
    conectado,
    error,
    marcarObsoleto,
    reconectar,
    limpiar,
  };
}
